import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Layout from "../components/Layout";
import { Check, Sparkles, Shield, Clock, ArrowRight, Loader2, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const priceSymbol = (c) => ({ usd: "$", aud: "A$", eur: "€", gbp: "£", cad: "C$", nzd: "NZ$" }[(c || "usd").toLowerCase()] || "$");

const Subscription = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pmStatus, setPmStatus] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);

  // Whether Stripe redirected back after a successful setup
  const paymentMethodAdded = searchParams.get("payment_method_added") === "true";

  useEffect(() => {
    fetchSubscription();
    fetchPaymentStatus();
  }, []);

  // Show success toast once on redirect back from Stripe setup
  useEffect(() => {
    if (paymentMethodAdded) {
      toast.success("Payment method added — you're all set for when your trial ends.");
      setSearchParams({}, { replace: true });
    }
  }, [paymentMethodAdded]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubData(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
      toast.error("Failed to load subscription info");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscription/payment-status`, { withCredentials: true });
      setPmStatus(res.data);
    } catch {
      // silently ignore — button simply won't show
    }
  };

  const handleSubscribe = async () => {
    setCheckoutLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/subscription/checkout`, {
        origin_url: originUrl
      }, { withCredentials: true });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout");
      setCheckoutLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    setSetupLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/setup-payment`, {
        origin_url: window.location.origin
      }, { withCredentials: true });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open payment setup");
      setSetupLoading(false);
    }
  };

  const [cancelLoading, setCancelLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const r = await axios.post(`${API}/subscription/cancel`, {});
      toast.success(r.data.message);
      setShowConfirm(false);
      await fetchSubscription();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation failed");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleUncancel = async () => {
    setCancelLoading(true);
    try {
      const r = await axios.post(`${API}/subscription/uncancel`, {});
      toast.success(r.data.message);
      await fetchSubscription();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not undo cancellation");
    } finally {
      setCancelLoading(false);
    }
  };

  const getDaysRemaining = () => {
    if (!subData?.subscription_expires) return 0;
    const expires = new Date(subData.subscription_expires);
    const now = new Date();
    const diff = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const features = [
    "AI-powered roster generation with GPT-5.2",
    "Unlimited employees and shift types",
    "Custom scheduling rules",
    "Time-off request management",
    "Calendar view with drag adjustments",
    "Fair weekend and shift distribution",
    "Priority support"
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const isActive = subData?.subscription_status === "active";
  const isTrial = subData?.subscription_status === "trial";
  const isExpired = subData?.subscription_status === "expired";
  const isPaused = subData?.subscription_status === "paused";
  const daysLeft = getDaysRemaining();

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Subscription</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Manage your roster.ai plan</p>
        </div>

        {/* Current Status */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1">
                Current Plan
              </p>
              <div className="flex items-center gap-3">
                <h2 className="text-xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">
                  {isTrial ? "Free Trial" : isActive ? "Pro Monthly" : isPaused ? "Trial Ended" : "Expired"}
                </h2>
                <span className={`
                  text-xs font-medium px-2 py-1 rounded capitalize
                  ${isActive ? 'bg-emerald-50 text-emerald-700' : 
                    isTrial ? 'bg-blue-50 text-blue-700' :
                    isPaused ? 'bg-amber-50 text-amber-700' :
                    'bg-red-50 text-red-700'}
                `}>
                  {subData?.subscription_status}
                </span>
              </div>
            </div>
            {(isTrial || isActive) && (
              <div className="text-right">
                <p className="text-3xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{daysLeft}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">days remaining</p>
              </div>
            )}
          </div>

          {isTrial && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded">
              <p className="text-sm text-blue-800">
                You're on a <strong>free 30-day trial</strong>. Subscribe to continue using roster.ai after your trial ends.
              </p>
            </div>
          )}
          {isPaused && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded space-y-3" data-testid="paused-banner">
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Your trial ended without a payment method</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Your access is paused. Add a card to resume your subscription — you'll be billed A$39/month going forward.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleAddPaymentMethod}
                disabled={setupLoading}
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="paused-add-payment-method-btn"
              >
                {setupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Add payment method to resume
              </Button>
            </div>
          )}
          {isExpired && (
            <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded">
              <p className="text-sm text-red-800">
                Your subscription has expired. Subscribe now to regain access to all features.
              </p>
            </div>
          )}
        </div>

        {/* Pricing Card */}
        <div className="bg-white dark:bg-zinc-900 border-2 border-[#002FA7] p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#002FA7]">
                  PRO PLAN
                </p>
                {subData?.introductory && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded-full text-[10px] font-bold uppercase tracking-wide" data-testid="sub-intro-badge">
                    <Sparkles className="w-2.5 h-2.5" /> {subData.introductory_label || "Introductory"}
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black tracking-tighter text-zinc-900 dark:text-zinc-100" data-testid="sub-price">
                  {subData ? (
                    <>{priceSymbol(subData.currency)}{Number(subData.price).toFixed(Number.isInteger(Number(subData.price)) ? 0 : 2)}</>
                  ) : (
                    <span className="inline-block h-9 w-24 bg-zinc-200 dark:bg-zinc-800 rounded animate-pulse" data-testid="sub-price-skeleton" />
                  )}
                </span>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">/month</span>
              </div>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Billed monthly in AUD · cancel anytime</p>
              {subData?.introductory && subData?.introductory_note && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 max-w-sm" data-testid="sub-intro-note">
                  {subData.introductory_note}
                </p>
              )}
            </div>
            <div className="w-12 h-12 bg-[#002FA7]/10 rounded flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-[#002FA7]" />
            </div>
          </div>

          <ul className="space-y-3 mb-8">
            {features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-5 h-5 bg-[#002FA7] rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-zinc-700 dark:text-zinc-300">{feature}</span>
              </li>
            ))}
          </ul>

          {isActive ? (
            <div className="space-y-3">
              {/* Trial with no payment method */}
              {pmStatus?.is_trialing && !pmStatus?.has_payment_method && !paymentMethodAdded && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded space-y-3" data-testid="add-pm-banner">
                  <div className="flex items-start gap-3">
                    <CreditCard className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">No payment method on file</p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        Your trial ends on <strong>{pmStatus.trial_end ? new Date(pmStatus.trial_end * 1000).toLocaleDateString() : "—"}</strong>.
                        Add a card now so your subscription continues automatically — no charge until then.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleAddPaymentMethod}
                    disabled={setupLoading}
                    className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white"
                    data-testid="add-payment-method-btn"
                  >
                    {setupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Add payment method
                  </Button>
                </div>
              )}

              {/* Payment method confirmed */}
              {(pmStatus?.has_payment_method || paymentMethodAdded) && pmStatus?.is_trialing && (
                <div className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded" data-testid="pm-on-file-banner">
                  <CreditCard className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">Payment method on file</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                      A${Number(subData?.price ?? 39).toFixed(0)}/month starts on{" "}
                      <strong>{pmStatus?.trial_end ? new Date(pmStatus.trial_end * 1000).toLocaleDateString() : "trial end"}</strong>.
                      No action needed.
                    </p>
                  </div>
                </div>
              )}

              {subData?.subscription_cancelled_at ? (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded space-y-3" data-testid="sub-cancelled-banner">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Cancellation scheduled</p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        Your access ends on <strong>{subData?.subscription_expires ? new Date(subData.subscription_expires).toLocaleDateString() : "—"}</strong> ({daysLeft} day{daysLeft === 1 ? "" : "s"} remaining).
                        After that, the subscription will not auto-renew.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleUncancel}
                    disabled={cancelLoading}
                    variant="outline"
                    className="w-full border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40"
                    data-testid="sub-uncancel-btn"
                  >
                    {cancelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Reactivate subscription
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded">
                    <Shield className="w-5 h-5 text-emerald-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">You're subscribed</p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">
                        Renews on {subData?.subscription_expires ? new Date(subData.subscription_expires).toLocaleDateString() : "N/A"}
                      </p>
                    </div>
                  </div>
                  {showConfirm ? (
                    <div className="p-4 border border-zinc-300 dark:border-zinc-700 rounded space-y-3" data-testid="sub-cancel-confirm">
                      <p className="text-sm text-zinc-700 dark:text-zinc-300">
                        Cancel your subscription? You'll keep access until <strong>the end of your current 28-day cycle</strong> — after that, AI roster generation and other paid features will be locked. You can reactivate anytime before then.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleCancel}
                          disabled={cancelLoading}
                          variant="destructive"
                          className="flex-1"
                          data-testid="sub-confirm-cancel-btn"
                        >
                          {cancelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                          Yes, cancel
                        </Button>
                        <Button
                          onClick={() => setShowConfirm(false)}
                          variant="outline"
                          disabled={cancelLoading}
                          className="flex-1"
                          data-testid="sub-keep-btn"
                        >
                          Keep subscription
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowConfirm(true)}
                      className="w-full text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors py-2"
                      data-testid="sub-cancel-btn"
                    >
                      Cancel subscription
                    </button>
                  )}
                </>
              )}
            </div>
          ) : isTrial && pmStatus?.is_trialing ? (
            /* Trial user who already has a Stripe subscription — just needs a card */
            <Button
              onClick={handleAddPaymentMethod}
              disabled={setupLoading}
              className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white font-medium py-6 text-base"
              data-testid="trial-add-payment-method-btn"
            >
              {setupLoading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Redirecting to Stripe...</>
              ) : (
                <><CreditCard className="w-5 h-5 mr-2" />Add payment method</>
              )}
            </Button>
          ) : (
            /* No Stripe subscription yet — create one via checkout */
            <Button
              onClick={handleSubscribe}
              disabled={checkoutLoading}
              className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white font-medium py-6 text-base"
              data-testid="subscribe-btn"
            >
              {checkoutLoading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Redirecting to checkout...</>
              ) : (
                <>Subscribe for {priceSymbol(subData?.currency)}{Number(subData?.price ?? 0).toFixed(Number.isInteger(Number(subData?.price ?? 0)) ? 0 : 2)}/month <ArrowRight className="w-5 h-5 ml-2" /></>
              )}
            </Button>
          )}
        </div>

        {/* FAQ */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 divide-y divide-zinc-200 dark:divide-zinc-800">
          <div className="p-6">
            <h3 className="text-lg tracking-tight font-medium text-zinc-900 dark:text-zinc-100">FAQ</h3>
          </div>
          {[
            { q: "How does the free trial work?", a: "When you sign up, you get 30 days of full access to all features at no cost. No credit card required during the trial." },
            { q: "What happens when my trial ends?", a: `After 30 days, you'll need to subscribe at ${priceSymbol(subData?.currency)}${Number(subData?.price ?? 0).toFixed(Number.isInteger(Number(subData?.price ?? 0)) ? 0 : 2)}/month to continue using roster.ai features like AI roster generation.` },
            { q: "Can I cancel anytime?", a: "Yes, you can cancel at any time. Your access will continue until the end of your current billing period." },
          ].map((item, idx) => (
            <div key={idx} className="p-6">
              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">{item.q}</p>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">{item.a}</p>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default Subscription;
